import { DialogDisclosure, Heading, HeadingLevel } from "@ariakit/react";
import {
  DialogTrigger,
  MiddotSeparated,
  MinWidth,
  useMediaQuery,
} from "@indietabletop/appkit";
import { Link, Route, Switch, useParams } from "wouter";
import { href } from "../App/href.tsx";
import warbandImageUrl from "../assets/warband.png";
import { useInvokeClient } from "../client.ts";
import { latest } from "../engine/latest.ts";
import { Warband } from "../engine/Warband.ts";
import { FailureView } from "../FailureView/FailureView.tsx";
import { ListView } from "../ListView/ListView.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { NotFoundPage } from "../NotFoundPage/NotFoundPage.tsx";
import {
  Page,
  PageContainer,
  PageContent,
  PageHeader,
  PageHeaderItem,
  PageHeaderTitle,
  SplitView,
} from "../oss/Layout/Layout.tsx";
import {
  PageHeaderBackButton,
  PageHeaderCloseButton,
} from "../PageHeaderButton/PageHeaderItem.tsx";
import { ShareWarbandDialog } from "../ShareWarbandDialog/ShareGangDialog.tsx";
import { SaveSharedWarband } from "./DuplicateWarband.tsx";
import { KnightListItem } from "./KnightListItem.tsx";
import { AddKnightDialog } from "./KnightFormDialog.tsx";
import { KnightMenu } from "./KnightMenu.tsx";
import { KnightView } from "./KnightView.tsx";
import { ReadOnlyContext, useIsReadOnly } from "./ReadOnlyContext.tsx";
import { illustration, view } from "./style.css.ts";
import { useLocalWarband } from "./useLocalWarband.tsx";
import { WarbandContext, useWarbandContext } from "./useWarbandContext.ts";
import { WarbandIssues } from "./ValidationIssues.tsx";
import { WarbandMenu } from "./WarbandMenu.tsx";

function WarbandView() {
  const warband = useWarbandContext();
  const isReadOnly = useIsReadOnly();

  return (
    <div className={view.container}>
      <div className={view.header}>
        <Heading className={view.heading}>{warband.name}</Heading>
        <MiddotSeparated className={view.summary}>
          <em>{warband.buildType}</em>
          {warband.summary}
        </MiddotSeparated>

        {!isReadOnly && (
          <div className={view.headerActions}>
            <Link
              href={href("~/builder/w/:warbandId/print", {
                path: { warbandId: warband.id },
              })}
              className={view.headerAction}
            >
              Print
            </Link>

            <DialogTrigger>
              <ShareWarbandDialog />

              <DialogDisclosure className={view.headerAction}>
                Share
              </DialogDisclosure>
            </DialogTrigger>
          </div>
        )}

        <WarbandIssues />
      </div>

      <ListView empty="Use the button below to add your first knight.">
        {warband.knights.map((knight) => {
          return <KnightListItem knight={knight} key={knight.id} />;
        })}
      </ListView>

      {isReadOnly ? (
        <DialogTrigger>
          <SaveSharedWarband />
          <DialogDisclosure className={view.action}>
            Save warband
          </DialogDisclosure>
        </DialogTrigger>
      ) : (
        <DialogTrigger>
          <AddKnightDialog
            backTo={href("~/builder/w/:warbandId", {
              path: { warbandId: warband.id },
            })}
          />
          <DialogDisclosure className={view.action}>
            Add knight
          </DialogDisclosure>
        </DialogTrigger>
      )}
    </div>
  );
}

function WarbandRoutes(props: { backHref: string; title: string }) {
  const { backHref, title } = props;

  const isWide = useMediaQuery(MinWidth.WIDE);

  if (isWide) {
    return (
      <HeadingLevel>
        <SplitView>
          <PageContainer>
            <PageHeader>
              <PageHeaderBackButton href={backHref} />
              <PageHeaderTitle>{title}</PageHeaderTitle>
              <PageHeaderItem>
                <WarbandMenu />
              </PageHeaderItem>
            </PageHeader>

            <PageContent>
              <WarbandView />
            </PageContent>
          </PageContainer>

          <PageContainer>
            <HeadingLevel>
              <Switch>
                <Route path="/">
                  <div className={illustration.container}>
                    <img
                      className={illustration.image}
                      src={warbandImageUrl}
                      alt=""
                    />
                  </div>
                </Route>

                <Route path="/k/:knightId">
                  <PageHeader>
                    <PageHeaderCloseButton href="/" />
                    <PageHeaderTitle>Knight</PageHeaderTitle>
                    <PageHeaderItem>
                      <KnightMenu />
                    </PageHeaderItem>
                  </PageHeader>

                  <PageContent>
                    <KnightView />
                  </PageContent>
                </Route>

                <Route>
                  <NotFoundPage />
                </Route>
              </Switch>
            </HeadingLevel>
          </PageContainer>
        </SplitView>
      </HeadingLevel>
    );
  }

  return (
    <Switch>
      <Route path="/">
        <Page
          backHref={backHref}
          title="Warband"
          actions={
            <PageHeaderItem>
              <WarbandMenu />
            </PageHeaderItem>
          }
        >
          <WarbandView />
        </Page>
      </Route>

      <Route path="/k/:knightId">
        <Page
          backHref="/"
          title="Knight"
          actions={
            <PageHeaderItem>
              <KnightMenu />
            </PageHeaderItem>
          }
        >
          <KnightView />
        </Page>
      </Route>

      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}

export function WarbandPage() {
  const result = useLocalWarband();
  const backHref = href("~/builder");

  if (result.isPending) {
    return (
      <Page backHref={backHref}>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page backHref={backHref}>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  return (
    <WarbandContext value={result.value}>
      <WarbandRoutes backHref={backHref} title="Warband" />
    </WarbandContext>
  );
}

export function SharedWarbandPage() {
  const params = useParams<{ warbandId: string }>();
  const [result] = useInvokeClient("getSnapshot", [
    "greathelm",
    params.warbandId,
  ]);
  const backHref = href("~/");

  if (result.isPending) {
    return (
      <Page backHref={backHref}>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page backHref={backHref}>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const warband = new Warband(latest, {
    ...result.value,
    snapshotId: null,
    deleted: false,
  });

  return (
    <ReadOnlyContext value={true}>
      <WarbandContext value={warband}>
        <WarbandRoutes backHref={backHref} title="Shared Warband" />
      </WarbandContext>
    </ReadOnlyContext>
  );
}
